// Coalescência dos eventos de drag do board (vuedraggable + SortableJS).
//
// Num move ENTRE colunas, o vuedraggable dispara DOIS eventos `change`: `added` na
// coluna de DESTINO e `removed` na de ORIGEM. No SortableJS (1.14) a ordem é
// `added` ANTES de `removed` (em sortable.esm.js o dispatch de `'add'` vem antes do
// de `'remove'`). Logo é IMPOSSÍVEL descobrir a origem dentro do handler de `added`:
// ela só é conhecida no `removed`, que chega depois.
//
// Este factory coalesce os dois lados — guarda o pedaço de cada evento e só dispara
// o move quando ORIGEM e DESTINO do MESMO card já chegaram, INDEPENDENTE da ordem em
// que os eventos cheguem. Sem isso, o `move_card` nunca é chamado e o card fica na
// coluna nova sem re-etiquetar (a etiqueta antiga não sai e a nova não entra).
//
// `performMove({ card, fromStageId, toStage, source })` é chamado quando o par fecha.
// `source` carrega { stageId, conversationId, element, oldIndex } para permitir
// reverter o move otimista em caso de erro.
export function createCardMoveHandler(performMove) {
  // Premissa: UM drag por vez (o SortableJS fecha o par `add`+`remove` de um drop
  // antes de iniciar o próximo). Cada novo evento sobrescreve seu slot, então um par
  // de cards divergentes (guarda em tryComplete) se auto-corrige no evento seguinte
  // sem disparar move indevido.
  let pendingAdd = null;
  let pendingRemove = null;

  const reset = () => {
    pendingAdd = null;
    pendingRemove = null;
  };

  const tryComplete = () => {
    if (!pendingAdd || !pendingRemove) return;
    // Só fecha o par quando os dois lados são do MESMO card.
    if (pendingAdd.card.id !== pendingRemove.conversationId) return;
    const { card, toStage } = pendingAdd;
    const source = pendingRemove;
    reset();
    // Mesmo estágio (reordenar dentro da própria coluna): nada a re-etiquetar.
    if (source.stageId === toStage.id) return;
    performMove({ card, fromStageId: source.stageId, toStage, source });
  };

  // vuedraggable @change: `added`/`removed` num move entre colunas; `moved` num
  // reordenar dentro da MESMA coluna (ignorado — não persistimos a ordem dos cards).
  const onCardChange = (event, stage) => {
    if (event.added) {
      pendingAdd = { card: event.added.element, toStage: stage };
      tryComplete();
    }
    if (event.removed) {
      pendingRemove = {
        stageId: stage.id,
        conversationId: event.removed.element.id,
        element: event.removed.element,
        oldIndex: event.removed.oldIndex,
      };
      tryComplete();
    }
  };

  return { onCardChange };
}
